import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { connect } from 'react-redux';
import {
  generatePath, RouteComponentProps, useHistory, withRouter,
} from 'react-router-dom';
import { isFinishedFetching, isFinishedCreating, ResourcesList } from 'redux-and-the-rest';
import BackArrowIcon from '@material-ui/icons/ArrowBack';

import { ApplicationState } from '../../redux/types';
import { GameModel } from '../../models/gameModel';
import { createGameScore, getOrFetchGameScores } from '../../redux/reducers/gameScore';
import { GameScoreModel } from '../../models/gameScoreModel';
import { Routes } from '../../routes';
import generateClientId from '../../redux/reducers/utils/generateClientId';
import ModalWrapper from '../modals/ModalWrapper';
import RewardClaim from '../rewards/RewardClaim/RewardClaim';

import './GamesViewScreen.scss';

interface Props {
  game: GameModel;
  islandId: string;
  gameScores: ResourcesList<GameScoreModel>;
}

const GameContainerView = ({
  game, islandId, gameScores,
}: Props): React.ReactElement => {
  const history = useHistory();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [hasClaimableReward, setHasClaimableReward] = useState(false);

  const handleCloseReward = () => {
    setHasClaimableReward(false);
  };

  // Handle game scrore message from iFrame
  const onMessage = useCallback(({ data: { correct, total } }: { data: { correct: number, total: number, origin: string
    }}) => {
    /**
       * This prevents messages fired by, say, React Dev Tools to be ignored.
       * Ideally the post message would come with a source property, but we
       * aren't in control of the games code. It's not ideal.
       */
    if (!correct && !total) {
      return;
    }

    createGameScore({
      islandId,
    },
    {
      id: generateClientId(),
      type: 'gameScore',
      game,
      score: correct,
      total,
    }, {});
  }, [game]);

  // Add a listener for game messages, remove on unmount
  useEffect(() => {
    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Once the game is loaded, set the iFrame source
  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.src = game.sourceUrl;
    }
  }, [game.status, iframeRef.current]);

  useEffect(() => {
    // Once the score is posted to the API, the game scores updates.
    // A Game score generate an available reward
    if (isFinishedFetching(gameScores)) {
      const newGameScore = gameScores.items.find((gameScore) => (isFinishedCreating(gameScore)
        && gameScore.values.chosenReward === null));

      // If we have any rewards to claim, we open the reward claim in a modal
      if (newGameScore) {
        setHasClaimableReward(true);
      }
    }
  }, [gameScores]);

  return (
    <div className="game-container">
      <iframe ref={iframeRef} className="game-iframe" title={game.name} />

      {hasClaimableReward && (
        <ModalWrapper headerTitle="Claim your Reward!">
          <RewardClaim onResumePlay={handleCloseReward} />
        </ModalWrapper>
      )}

      <button
        onClick={() => {
          history.push(generatePath(Routes.Islands.View, { id: islandId }));
        }}
        type="button"
        className="button primary icon-button back-button"
      >
        <BackArrowIcon className="icon" />
      </button>
    </div>
  );
};

const mapStateToProps = (
  { gameScores }: ApplicationState,
  { match: { params: { islandId } } }: RouteComponentProps<{ gameId: string; islandId: string; }>,

) => ({
  gameScores: getOrFetchGameScores(gameScores, {
    include: 'game,chosenReward,rewardsToChooseFrom',
    islandId,
  }),
});

export default withRouter(connect(mapStateToProps)(GameContainerView));
